import { TranslateService } from "@ngx-translate/core";
import { ChartDataset } from "chart.js";
import { ChartConstants } from "src/app/shared/shared";
import { ColorUtils } from "src/app/shared/utils/color/color.utils";
import { ChartAxis, TimeOfUseTariffUtils, Utils } from "src/app/shared/utils/utils";

export namespace Controller_Ess_TimeOfUseTariffUtils {

  export type ScheduleChartData = {
    datasets: ChartDataset[],
    colors: any[],
    labels: Date[]
  };

  export const STATE_TIMELINE_Y_AXIS_ID = "stateTimeline";
  export const STATE_TIMELINE_AXIS_MIN = 0;
  export const STATE_TIMELINE_AXIS_MAX = 20;
  export const STATE_TIMELINE_VALUE = 1;
  export const STATE_TIMELINE_DATASET_LABEL = "Mode";

  export enum ControlMode {
    CHARGE_CONSUMPTION = "CHARGE_CONSUMPTION",
    DELAY_DISCHARGE = "DELAY_DISCHARGE",
    DISCHARGE_TO_GRID = "DISCHARGE_TO_GRID",
  }

  export function getControlMode(allowChargeFromGrid: boolean, enableDischargeGrid: boolean): ControlMode {
    if (enableDischargeGrid) {
      return ControlMode.DISCHARGE_TO_GRID;
    }
    if (allowChargeFromGrid) {
      return ControlMode.CHARGE_CONSUMPTION;
    }
    return ControlMode.DELAY_DISCHARGE;
  }

  enum StateValue {
    DELAY_DISCHARGE = 0,
    BALANCING = 1,
    CHARGE_GRID = 3,
    DISCHARGE_GRID = 4,
    PEAK_SHAVING = 5,
    HOLD = 6,
  }

  export function getScheduleChartData(
    size: number,
    prices: Array<number | null | undefined>,
    states: Array<number | null | undefined>,
    timestamps: string[],
    gridBuy: Array<number | null | undefined>,
    gridSell: Array<number | null | undefined>,
    socArray: Array<number | null | undefined>,
    translate: TranslateService,
    controlMode: Controller_Ess_TimeOfUseTariffUtils.ControlMode,
    consumptionPrices?: Array<number | null | undefined>,
    productionPrices?: Array<number | null | undefined>,
    productionArray?: Array<number | null | undefined>,
    consumptionArray?: Array<number | null | undefined>,
    essArray?: Array<number | null | undefined>,
  ): Controller_Ess_TimeOfUseTariffUtils.ScheduleChartData {

    const datasets: ChartDataset[] = [];
    const colors: any[] = [];
    const labels: Date[] = [];

    const effectiveConsumptionPrices = consumptionPrices ?? prices;
    const effectiveProductionPrices = productionPrices ?? prices;

    const modeTimelineData: Array<number | null> = Array(size).fill(null);
    const modeTimelineBackgroundColors: string[] = [];
    const modeTimelineBorderColors: string[] = [];

    for (let index = 0; index < size; index++) {
      const state = toNumberOrNull(states[index]);

      labels.push(new Date(timestamps[index]));

      if (state === null) {
        modeTimelineBackgroundColors.push("rgba(0,0,0,0)");
        modeTimelineBorderColors.push("rgba(0,0,0,0)");
        continue;
      }

      modeTimelineData[index] = STATE_TIMELINE_VALUE;

      const modeColor = getModeColor(state);
      modeTimelineBackgroundColors.push(modeColor.backgroundColor);
      modeTimelineBorderColors.push(modeColor.borderColor);
    }

    datasets.push({
      type: "bar",
      label: STATE_TIMELINE_DATASET_LABEL,
      data: modeTimelineData,
      hidden: false,
      order: -100,
      yAxisID: STATE_TIMELINE_Y_AXIS_ID,
      grouped: false,
      barPercentage: 1,
      categoryPercentage: 1,
      borderSkipped: false,
      backgroundColor: modeTimelineBackgroundColors,
      borderColor: modeTimelineBorderColors,
    });

    colors.push({
      backgroundColor: modeTimelineBackgroundColors,
      borderColor: modeTimelineBorderColors,
    });

    datasets.push({
      type: "bar",
      label: translate.instant("GENERAL.GRID_BUY_PRICE"),
      data: effectiveConsumptionPrices.map(v => formatPriceOrNull(v)),
      hidden: false,
      yAxisID: ChartAxis.LEFT,
      order: 5,
      stack: "consumption",
      grouped: true,
      barPercentage: 0.85,
      categoryPercentage: 0.75,
      borderSkipped: false,
    });
    colors.push({
      backgroundColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.YELLOW, 1),
      borderColor: ChartConstants.Colors.YELLOW,
    });

    datasets.push({
      type: "bar",
      label: translate.instant("GENERAL.GRID_SELL_PRICE"),
      data: effectiveProductionPrices.map(v => formatPriceOrNull(v)),
      hidden: false,
      yAxisID: ChartAxis.LEFT,
      order: 5,
      stack: "production",
      grouped: true,
      barPercentage: 0.85,
      categoryPercentage: 0.75,
      borderSkipped: false,
    });
    colors.push({
      backgroundColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.BLUE, 1),
      borderColor: ChartConstants.Colors.BLUE,
    });

    datasets.push({
      type: "line",
      label: translate.instant("GENERAL.SOC"),
      data: socArray.map(v => toNumberOrNull(v)),
      hidden: false,
      yAxisID: ChartAxis.RIGHT,
      borderDash: [10, 10],
      order: 0,
    });
    colors.push(chartColor(ChartConstants.Colors.BLUE_GREY, 0.25, 0.9));

    datasets.push({
      type: "line",
      label: translate.instant("GENERAL.GRID_BUY_ADVANCED"),
      data: gridBuy.map(v => powerWattToKilowatt(v)),
      hidden: true,
      yAxisID: ChartAxis.RIGHT_2,
      order: 2,
    });
    colors.push(blackColor(0.20, 0.90));

    datasets.push({
      type: "line",
      label: translateOrFallback(translate, "GENERAL.GRID_SELL", "Grid sell"),
      data: gridSell.map(v => powerWattToKilowatt(v)),
      hidden: false,
      yAxisID: ChartAxis.RIGHT_2,
      borderDash: [5, 5],
      order: 2,
    });
    colors.push(blackColor(0.12, 0.70));

    if (productionArray != null && !productionArray.every(v => v === null || v === undefined)) {
      datasets.push({
        type: "line",
        label: translateOrFallback(translate, "GENERAL.PRODUCTION", "Production"),
        data: productionArray.map(v => powerWattToKilowatt(v)),
        hidden: true,
        yAxisID: ChartAxis.RIGHT_2,
        order: 3,
      });
      colors.push(chartColorByName("BLUE", ChartConstants.Colors.BLUE_GREY, 0.15, 0.9));
    }

    if (consumptionArray != null && !consumptionArray.every(v => v === null || v === undefined)) {
      datasets.push({
        type: "line",
        label: translateOrFallback(translate, "GENERAL.CONSUMPTION", "Consumption"),
        data: consumptionArray.map(v => powerWattToKilowatt(v)),
        hidden: true,
        yAxisID: ChartAxis.RIGHT_2,
        borderDash: [5, 5],
        order: 3,
      });
      colors.push(chartColorByName("YELLOW", ChartConstants.Colors.BLUE_GREY, 0.15, 0.9));
    }

    if (essArray != null && !essArray.every(v => v === null || v === undefined)) {
      datasets.push({
        type: "line",
        label: translateOrFallback(translate, "GENERAL.ESS", "ESS"),
        data: essArray.map(v => powerWattToKilowatt(v)),
        hidden: true,
        yAxisID: ChartAxis.RIGHT_2,
        borderDash: [10, 5],
        order: 3,
      });
      colors.push(chartColor(ChartConstants.Colors.BLUE_GREY, 0.10, 0.6));
    }

    return {
      colors: colors,
      datasets: datasets,
      labels: labels,
    };
  }

  function getModeColor(state: number): { backgroundColor: string, borderColor: string } {
    switch (state) {
      case StateValue.BALANCING:
        return {
          backgroundColor: "rgba(51,102,0,0.8)",
          borderColor: "rgba(51,102,0,1)",
        };

      case StateValue.CHARGE_GRID:
        return {
          backgroundColor: "rgba(0,204,204,0.5)",
          borderColor: "rgba(0,204,204,0.7)",
        };

      case StateValue.DELAY_DISCHARGE:
        return {
          backgroundColor: "rgba(0,0,0,0.8)",
          borderColor: "rgba(0,0,0,0.9)",
        };

      case StateValue.PEAK_SHAVING:
        return {
          backgroundColor: "rgba(255,153,0,0.6)",
          borderColor: "rgba(255,153,0,0.9)",
        };

      case StateValue.DISCHARGE_GRID:
        return {
          backgroundColor: "rgba(153,0,204,0.5)",
          borderColor: "rgba(153,0,204,0.9)",
        };

      case StateValue.HOLD:
        return {
          backgroundColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.BLUE_GREY, 0.35),
          borderColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.BLUE_GREY, 0.9),
        };

      default:
        return {
          backgroundColor: "rgba(0,0,0,0)",
          borderColor: "rgba(0,0,0,0)",
        };
    }
  }

  function formatPriceOrNull(value: number | null | undefined): number | null {
    const numberValue = toNumberOrNull(value);
    if (numberValue === null) {
      return null;
    }

    return TimeOfUseTariffUtils.formatPrice(numberValue);
  }

  function powerWattToKilowatt(value: number | null | undefined): number | null {
    const numberValue = toNumberOrNull(value);
    if (numberValue === null) {
      return null;
    }

    return Utils.divideSafely(numberValue, 1000);
  }

  function toNumberOrNull(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  function translateOrFallback(translate: TranslateService, key: string, fallback: string): string {
    const translated = translate.instant(key);
    return translated === key ? fallback : translated;
  }

  function chartColor(
    rgb: string,
    backgroundOpacity = ChartConstants.Colors.LEGEND_LABEL_BG_OPACITY,
    borderOpacity = 1,
  ) {
    return {
      backgroundColor: ColorUtils.rgbStringToRgba(rgb, backgroundOpacity),
      borderColor: ColorUtils.rgbStringToRgba(rgb, borderOpacity),
    };
  }

  function chartColorByName(
    colorName: string,
    fallbackColor: string,
    backgroundOpacity = ChartConstants.Colors.LEGEND_LABEL_BG_OPACITY,
    borderOpacity = 1,
  ) {
    const colors = ChartConstants.Colors as any;
    const color = colors[colorName] ?? fallbackColor;

    return chartColor(color, backgroundOpacity, borderOpacity);
  }

  function blackColor(backgroundOpacity: number, borderOpacity: number) {
    return {
      backgroundColor: `rgba(0,0,0,${backgroundOpacity})`,
      borderColor: `rgba(0,0,0,${borderOpacity})`,
    };
  }
}
